import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { DefaultRedirect } from '../components/DefaultRedirect';
import {
  useUser,
  userManager,
  savePostLoginRedirectFromCurrentLocation
} from '../components/OidcProvider/OidcProvider';
import LoadingSpinner from '../components/LoadingSpinner';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { FrontChannelLogout } from '../components/FrontChannelLogout';
import { UsersRoutes } from './UsersRoutes';
import { CustomContentRoutes } from './CustomContentRoutes';

import { LegacySamplePreviewGuard } from '../components/LegacySamplePreview/LegacySamplePreviewGuard';
import { LEGACY_SAMPLE_PREVIEW_BASE_PATH } from '../constants/legacySamplePreview';

const Dashboard = lazy(() => import('../pages/Dashboard').then(m => ({ default: m.Dashboard })));
const CoursesPage = lazy(() => import('../pages/CoursesPage').then(m => ({ default: m.CoursesPage })));
const SamplePreviewPage = lazy(() =>
  import('../pages/SamplePreviewPage').then((m) => ({ default: m.SamplePreviewPage })),
);
const SettingsPage = lazy(() => import('../pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const CourseDetail = lazy(() => import('../pages/CourseDetail'));
const CourseLayout = lazy(() => import('../components/Layout/CourseLayout'));
const CustomContentPage = lazy(() => import('../pages/CustomContentPage').then(m => ({ default: m.CustomContentPage })));
const AddCustomContentPage = lazy(() => import('../pages/AddCustomContentPage').then(m => ({ default: m.AddCustomContentPage })));
const EditCustomContentPage = lazy(() => import('../pages/EditCustomContentPage').then(m => ({ default: m.EditCustomContentPage })));
const ViewCustomContentPage = lazy(() => import('../pages/ViewCustomContentPage').then(m => ({ default: m.ViewCustomContentPage })));
const AccountsPage = lazy(() => import('../pages/AccountsPage').then(m => ({ default: m.AccountsPage })));
const EditAccountPage = lazy(() => import('../pages/EditAccountPage').then(m => ({ default: m.EditAccountPage })));
const UsersPage = lazy(() => import('../pages/UsersPage').then(m => ({ default: m.UsersPage })));
const EditUserPage = lazy(() => import('../pages/EditUserPage').then(m => ({ default: m.EditUserPage })));
const ReviewContentPage = lazy(() => import('../pages/ReviewContentPage').then(m => ({ default: m.ReviewContentPage })));
const UnauthorizedPage = lazy(() => import('../pages/UnauthorizedPage').then(m => ({ default: m.UnauthorizedPage })));
const AIRecommendationPage = lazy(() => import('../pages/AIRecommendationPage').then(m => ({ default: m.AIRecommendationPage })));
const AiRecommendationReportDownloadPage = lazy(() =>
  import('../pages/AiRecommendationReportDownloadPage').then(m => ({ default: m.AiRecommendationReportDownloadPage })),
);
const OnboardingAcceptPage = lazy(() => import('../pages/OnboardingAcceptPage').then(m => ({ default: m.OnboardingAcceptPage })));
const TemplatesPage = lazy(() => import('../pages/TemplatesPage').then(m => ({ default: m.TemplatesPage })));
const NewTemplatePage = lazy(() => import('../pages/NewTemplatePage').then(m => ({ default: m.NewTemplatePage })));
const NewStandaloneTemplatePage = lazy(() =>
  import('../pages/NewStandaloneTemplatePage').then(m => ({ default: m.NewStandaloneTemplatePage })),
);
const DuplicateTemplatePage = lazy(() =>
  import('../pages/DuplicateTemplatePage').then((m) => ({ default: m.DuplicateTemplatePage })),
);
const DuplicateCoursePage = lazy(() =>
  import('../pages/DuplicateCoursePage').then((m) => ({ default: m.DuplicateCoursePage })),
);
const NewCocModuleTemplatePage = lazy(() =>
  import('../pages/NewCocModuleTemplatePage').then(m => ({ default: m.NewCocModuleTemplatePage })),
);
const NewCocTrainingTemplatePage = lazy(() =>
  import('../pages/NewCocTrainingTemplatePage').then(m => ({ default: m.NewCocTrainingTemplatePage })),
);
const NewCocTrainingOrderPage = lazy(() =>
  import('../pages/NewCocTrainingOrderPage').then(m => ({ default: m.NewCocTrainingOrderPage })),
);
const AuthoringCourseTranslationsPage = lazy(() =>
  import('../pages/AuthoringCourseTranslationsPage').then((m) => ({
    default: m.AuthoringCourseTranslationsPage,
  })),
);
const AuthoringPlaceholderPage = lazy(() =>
  import('../pages/AuthoringPlaceholderPage').then((m) => ({ default: m.AuthoringPlaceholderPage })),
);
const AuthoringCoursesPage = lazy(() =>
  import('../pages/AuthoringCoursesPage').then((m) => ({ default: m.AuthoringCoursesPage })),
);
const NewCoursePage = lazy(() =>
  import('../pages/NewCoursePage').then((m) => ({ default: m.NewCoursePage })),
);
const NewStandaloneCoursePage = lazy(() =>
  import('../pages/NewStandaloneCoursePage').then((m) => ({ default: m.NewStandaloneCoursePage })),
);
const NewCocTrainingCoursePage = lazy(() =>
  import('../pages/NewCocTrainingCoursePage').then((m) => ({ default: m.NewCocTrainingCoursePage })),
);
const AuthoringCourseEditorPage = lazy(() =>
  import('../pages/AuthoringCourseEditorPage').then((m) => ({ default: m.AuthoringCourseEditorPage })),
);
const StandaloneTemplateEditorPage = lazy(() =>
  import('../pages/StandaloneTemplateEditorPage').then(m => ({ default: m.StandaloneTemplateEditorPage })),
);

export function AppRoutes() {
  const { userProfile, isLoading, isLoggingOut } = useUser();

  useEffect(() => {
    // Don't auto-redirect to login if user is in the process of logging out
    if (!isLoading && !userProfile && !isLoggingOut) {
      savePostLoginRedirectFromCurrentLocation();
      userManager.signinRedirect();
    }
  }, [
    userProfile,
    isLoading,
    isLoggingOut,
    savePostLoginRedirectFromCurrentLocation,
    userManager
  ]);

  if (isLoading) {
    return null;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* OIDC Callback Route - handled by OidcProvider, show loading spinner */}
        <Route path="/Login/Callback" element={<LoadingSpinner />} />

        {/* Front-channel logout endpoint - called by OIDC provider during logout */}
        <Route path="/logout" element={<FrontChannelLogout />} />

        {/* Unauthorized page - accessible to all authenticated users */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {userProfile ? (
          <Route path="/onboarding/accept" element={<OnboardingAcceptPage />} />
        ) : null}

        <Route path="/" element={<Layout />}>
          {userProfile ? (
            <>
              <Route index element={<DefaultRedirect />} />
              {/* Protected dashboard route - super_admin, account_admin */}
              <Route
                path="review-center"
                element={
                  <ProtectedRoute requiredAccess="dashboard">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              {/* Protected courses route - Standard users only */}
              <Route
                path="courses"
                element={
                  <ProtectedRoute requiredAccess="courses">
                    <CoursesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sample-preview"
                element={<Navigate to={LEGACY_SAMPLE_PREVIEW_BASE_PATH} replace />}
              />
              <Route
                path="sample-preview/*"
                element={<Navigate to={LEGACY_SAMPLE_PREVIEW_BASE_PATH} replace />}
              />
              <Route
                path="courses/introducing-compliance-education"
                element={
                  <ProtectedRoute requiredAccess="courses">
                    <LegacySamplePreviewGuard>
                      <SamplePreviewPage />
                    </LegacySamplePreviewGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="courses/introducing-compliance-education/:courseId/*"
                element={
                  <ProtectedRoute requiredAccess="courses">
                    <LegacySamplePreviewGuard>
                      <CourseLayout />
                    </LegacySamplePreviewGuard>
                  </ProtectedRoute>
                }
              >
                <Route path="*" element={<CourseDetail />} />
              </Route>
              <Route
                path="settings"
                element={
                  <ProtectedRoute requiredAccess="settings">
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="courses/:courseId/*"
                element={
                  <ProtectedRoute requiredAccess="courses">
                    <CourseLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="*" element={<CourseDetail />} />
              </Route>
              {/* Protected accounts routes - super_admin, account_admin */}
              <Route
                path="accounts"
                element={
                  <ProtectedRoute requiredAccess="accounts">
                    <AccountsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="accounts/edit/:accountId"
                element={
                  <ProtectedRoute requiredAccess="accounts">
                    <EditAccountPage />
                  </ProtectedRoute>
                }
              />
              {/* AI Recommendation Route - Dev environment only, super_admin or account_admin access */}
              <Route
                path="ai-recommendation"
                element={
                  <ProtectedRoute requiredAccess="aiRecommendation">
                    <AIRecommendationPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Secure attachment deep-link landing page (CEE-929). Reached via the
                training plan email link. Spark super_admin and presales AI users only;
                API enforces download authorization (403/404).
              */}
              <Route
                path="training-plans/reports/:attachmentId"
                element={
                  <ProtectedRoute requiredAccess="trainingPlanGateway">
                    <AiRecommendationReportDownloadPage />
                  </ProtectedRoute>
                }
              />
              {/* Protected content authoring templates route - super_admin only */}
              <Route
                path="templates"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <TemplatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="templates/new/standalone"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <NewStandaloneTemplatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="authoring/courses"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <AuthoringCoursesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="authoring/courses/new"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <NewCoursePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="authoring/courses/new/standalone"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <NewStandaloneCoursePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="authoring/courses/new/coc-training"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <NewCocTrainingCoursePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="authoring/courses/:courseId/duplicate"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <DuplicateCoursePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="authoring/courses/:courseId/translations"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <AuthoringCourseTranslationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="authoring/courses/:courseId/content"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <AuthoringCourseEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="templates/new/coc-module"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <NewCocModuleTemplatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="templates/new/coc-training/order"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <NewCocTrainingOrderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="templates/new/coc-training"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <NewCocTrainingTemplatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="templates/new"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <NewTemplatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="templates/:templateId/duplicate"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <DuplicateTemplatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="templates/:templateId/content"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <StandaloneTemplateEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="authoring/media-collection"
                element={
                  <ProtectedRoute requiredAccess="templates">
                    <AuthoringPlaceholderPage
                      titleKey="page.authoring_media_collection.title"
                      subtitleKey="page.authoring_media_collection.subtitle"
                    />
                  </ProtectedRoute>
                }
              />
              {/* Protected users routes - super_admin only */}
              {/* UsersRoutes wraps these routes with UsersProvider context */}
              <Route element={<UsersRoutes />}>
                <Route
                  path="operators"
                  element={
                    <ProtectedRoute requiredAccess="users">
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="operators/edit/:userId"
                  element={
                    <ProtectedRoute requiredAccess="users">
                      <EditUserPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              {/* Protected review content route - super_admin, account_admin */}
              <Route
                path="review-center/review-content/:contentId"
                element={
                  <ProtectedRoute requiredAccess="reviewContent">
                    <ReviewContentPage />
                  </ProtectedRoute>
                }
              />
              {/* Protected content management routes - super_admin, content_manager */}
              {/* CustomContentRoutes wraps these routes with CustomContentProvider context */}
              <Route element={<CustomContentRoutes />}>
                <Route
                  path="manage-content"
                  element={
                    <ProtectedRoute requiredAccess="contentManagement">
                      <CustomContentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="manage-content/add"
                  element={
                    <ProtectedRoute requiredAccess="contentManagement">
                      <AddCustomContentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="manage-content/add/details"
                  element={
                    <ProtectedRoute requiredAccess="contentManagement">
                      <EditCustomContentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="manage-content/:id"
                  element={
                    <ProtectedRoute requiredAccess="contentManagement">
                      <ViewCustomContentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="manage-content/:id/edit"
                  element={
                    <ProtectedRoute requiredAccess="contentManagement">
                      <EditCustomContentPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </>
          ) : null}
        </Route>
      </Routes>
    </Suspense>
  );
}
